import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.#abortController = new AbortController();
    // Listen on window in the capture phase. The accelerated-checkout provider
    // registers a capture handler on document that calls stopPropagation(), which
    // halts the event before it reaches anything further down - a listener on this
    // element never runs, in either phase. Window capture fires before document
    // capture, so this is the one place the click is reliably observable.
    window.addEventListener('click', this.#handleCheckoutClick, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Close the drawer when any checkout control is used.
   *
   * The drawer is a modal <dialog>, so it sits in the browser's top layer and
   * paints above every z-index on the page. Accelerated checkouts that open
   * their own full-screen overlay (Breeze, Super Money, Just Pay) therefore
   * appeared *behind* the drawer and could not be used. Closing the drawer
   * takes it out of the top layer and lets that overlay take over the screen.
   *
   * The close is deferred by a tick so the provider's own click handler runs
   * first on a drawer that is still in the DOM.
   *
   * @param {Event} event
   */
  #handleCheckoutClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const checkoutControl = target.closest(
      '#checkout, .cart__checkout-button, .additional-checkout-buttons, [name="checkout"]'
    );
    // Only react to checkout controls inside this drawer, not the cart page's own.
    if (!checkoutControl || !this.contains(checkoutControl)) return;

    // Let the provider open its overlay first, then drop the drawer out of the
    // top layer. A short delay rather than a microtask, because the provider
    // mounts its overlay asynchronously.
    setTimeout(() => this.close(), 150);
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
